import {
  emit
} from "./chunk.HM4ZRC4T.js";
import {
  watch
} from "./chunk.HBM5K2JV.js";
import {
  __decorateClass
} from "./chunk.U4VUMZVF.js";

// src/utilities/checkable-form-element/checkable-form-element.ts
import { css, html, LitElement } from "lit";
import { property, query, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { live } from "lit/directives/live.js";
var CheckableFormElement = class extends LitElement {
  constructor() {
    super(...arguments);
    this._checked = false;
    this.inputId = "";
    this.labelId = "";
    this.hasFocus = false;
    this.labelColor = "primary" /* primary */;
    this.subtitleColor = "light" /* light */;
    this.name = "";
    this.value = "";
    this.label = "";
    this.subtitle = "";
    this.labelSize = "small" /* small */;
    this.tooltipText = "";
    this.disabled = false;
    this.required = false;
    this.invalid = false;
  }
  get checked() {
    return this._checked;
  }
  set checked(newChecked) {
    if (this.checked !== newChecked) {
      this.setChecked(newChecked);
    }
  }
  setChecked(newChecked, { emitEvent } = { emitEvent: true }) {
    this.setNewCheckedValue(newChecked);
    if (emitEvent) {
      emit(this, "yotpo-change" /* change */, { detail: this.checked });
    }
  }
  firstUpdated() {
    this.updateComplete.then(() => {
      this.handleDisabledChange();
    });
  }
  setInputLabel(labelId, inputId) {
    this.labelId = labelId;
    this.inputId = inputId;
  }
  click() {
    this.input.click();
  }
  focus(options) {
    this.input.focus(options);
  }
  blur() {
    this.input.blur();
  }
  handleInputClick(event) {
    event.stopPropagation();
  }
  handleInputKeyDown(event) {
  }
  handleInputBlur() {
    this.hasFocus = false;
    emit(this, "yotpo-blur" /* blur */);
  }
  handleInputFocus() {
    this.hasFocus = true;
    emit(this, "yotpo-focus" /* focus */);
  }
  handleLabelLinkAction(event) {
    event.stopPropagation();
    emit(this, "yotpo-link-action" /* linkAction */, { detail: event.detail });
  }
  handleDisabledChange() {
    if (this.input) {
      this.input.disabled = this.disabled;
      this.invalid = !this.input.checkValidity();
    }
    this.labelColor = this.disabled ? "disabled" /* disabled */ : "primary" /* primary */;
    this.subtitleColor = this.disabled ? "disabled" /* disabled */ : "light" /* light */;
  }
  setNewCheckedValue(newChecked) {
    if (this.input) {
      this.invalid = !this.input.checkValidity();
    }
    this._checked = newChecked;
    this.toggleAttribute("checked", newChecked);
  }
  renderCheckableFormElement(inputData, inputAddition) {
    return html`
			<label
				part="base"
				id=${this.labelId}
				for=${this.inputId}
				class=${classMap({
      checkable_form_element: true,
      "checkable_form_element--checked": this.checked,
      "checkable_form_element--disabled": this.disabled,
      "checkable_form_element--focused": this.hasFocus,
      "checkable_form_element--with-subtitle": this.subtitle,
      "checkable_form_element--small": this.labelSize === "small" /* small */,
      "checkable_form_element--medium": this.labelSize === "medium" /* medium */,
      "checkable_form_element--large": this.labelSize === "large" /* large */
    })}
			>
				<input
					id=${ifDefined(this.inputId)}
					class=${inputData.class}
					type=${inputData.type}
					role=${inputData.role}
					data-cy=${ifDefined(inputData.dataCy)}
					name=${ifDefined(this.name)}
					value=${ifDefined(this.value)}
					.checked=${live(this.checked)}
					.disabled=${this.disabled}
					.required=${this.required}
					aria-checked=${this.checked ? "true" : "false"}
					aria-disabled=${this.disabled ? "true" : "false"}
					aria-labelledby=${ifDefined(this.labelId)}
					@click=${this.handleInputClick}
					@keydown=${this.handleInputKeyDown}
					@blur=${this.handleInputBlur}
					@focus=${this.handleInputFocus}
				/>

				${inputAddition}
				${this.label ? html`<yotpo-info-label
							part="label"
							class="checkable_form_element__label"
							labelColor=${this.labelColor}
							subtitleColor=${this.subtitleColor}
							labelSize=${this.labelSize}
							text=${this.label}
							subtitle=${this.subtitle}
							tooltipText=${this.tooltipText}
							@yotpo-link-action=${this.handleLabelLinkAction}
					  ></yotpo-info-label>` : ""}
			</label>
		`;
  }
};
__decorateClass([
  query('[part="base"]')
], CheckableFormElement.prototype, "base", 2);
__decorateClass([
  query("input")
], CheckableFormElement.prototype, "input", 2);
__decorateClass([
  state()
], CheckableFormElement.prototype, "_checked", 2);
__decorateClass([
  state()
], CheckableFormElement.prototype, "inputId", 2);
__decorateClass([
  state()
], CheckableFormElement.prototype, "labelId", 2);
__decorateClass([
  state()
], CheckableFormElement.prototype, "hasFocus", 2);
__decorateClass([
  state()
], CheckableFormElement.prototype, "labelColor", 2);
__decorateClass([
  state()
], CheckableFormElement.prototype, "subtitleColor", 2);
__decorateClass([
  property({ reflect: true })
], CheckableFormElement.prototype, "name", 2);
__decorateClass([
  property({ type: String })
], CheckableFormElement.prototype, "value", 2);
__decorateClass([
  property({ reflect: true })
], CheckableFormElement.prototype, "label", 2);
__decorateClass([
  property({ reflect: true })
], CheckableFormElement.prototype, "subtitle", 2);
__decorateClass([
  property({ reflect: true })
], CheckableFormElement.prototype, "labelSize", 2);
__decorateClass([
  property({ reflect: true })
], CheckableFormElement.prototype, "tooltipText", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], CheckableFormElement.prototype, "disabled", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], CheckableFormElement.prototype, "required", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], CheckableFormElement.prototype, "invalid", 2);
__decorateClass([
  property({ type: Boolean })
], CheckableFormElement.prototype, "checked", 1);
__decorateClass([
  watch("disabled", { waitUntilFirstUpdate: true })
], CheckableFormElement.prototype, "handleDisabledChange", 1);
var checkableFormElementStyles = css`
	:host {
		display: inline-block;
	}

	.checkable_form_element {
		display: inline-flex;
		align-items: flex-start;
		font-family: var(--yotpo-font-inter);
		column-gap: var(--yotpo-input-spacing);
		vertical-align: middle;
		cursor: pointer;
	}

	.checkable_form_element.checkable_form_element--medium [part='control'],
	.checkable_form_element.checkable_form_element--large [part='control'] {
		margin-top: var(--yotpo-spacing-tiny);
	}

	/* Disabled */
	.checkable_form_element--disabled {
		cursor: not-allowed;
	}

	.checkable_form_element__label {
		user-select: none;
	}
`;
var heightLimitedFromElementStyles = css`
	.checkable_form_element.checkable_form_element--small:not(.checkable_form_element--with-subtitle),
	.checkable_form_element.checkable_form_element--small:not(.checkable_form_element--with-subtitle)
		.checkable_form_element__label {
		height: var(--yotpo-toggle-size);
	}

	.checkable_form_element.checkable_form_element--small.checkable_form_element--with-subtitle,
	.checkable_form_element.checkable_form_element--small.checkable_form_element--with-subtitle
		.checkable_form_element__label {
		height: var(--yotpo-two-line-toggle-size);
	}

	.checkable_form_element.checkable_form_element--medium:not(.checkable_form_element--with-subtitle),
	.checkable_form_element.checkable_form_element--medium:not(.checkable_form_element--with-subtitle)
		.checkable_form_element__label,
	.checkable_form_element.checkable_form_element--large:not(.checkable_form_element--with-subtitle),
	.checkable_form_element.checkable_form_element--large:not(.checkable_form_element--with-subtitle)
		.checkable_form_element__label {
		height: var(--yotpo-bigger-toggle-size);
	}

	.checkable_form_element.checkable_form_element--medium.checkable_form_element--with-subtitle,
	.checkable_form_element.checkable_form_element--medium.checkable_form_element--with-subtitle
		.checkable_form_element__label,
	.checkable_form_element.checkable_form_element--large.checkable_form_element--with-subtitle,
	.checkable_form_element.checkable_form_element--large.checkable_form_element--with-subtitle
		.checkable_form_element__label {
		height: var(--yotpo-bigger-two-line-toggle-size);
	}
`;

export {
  CheckableFormElement,
  checkableFormElementStyles,
  heightLimitedFromElementStyles
};
